/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { WittyFormContextProvider } from '@agilelab/plugin-wb-witty-react';
import {
  errorApiRef,
  useAnalytics,
  useApi,
  useRouteRefParams,
} from '@backstage/core-plugin-api';
import { JsonObject } from '@backstage/types';
import {
  Box,
  Button,
  StepButton,
  StepContent,
  Stepper,
  Step as StepUI,
  Typography,
  useTheme,
} from '@material-ui/core';
import { FormProps, IChangeEvent, withTheme } from '@rjsf/core';
import { Theme as MuiTheme } from '@rjsf/material-ui';
import { Fragment, MutableRefObject, useEffect, useRef, useState } from 'react';
import { useDependencyManagedForm } from './useDependencyManagedForm';
import { FormExtraConfigContextProvider } from '../../contexts/FormExtraConfigContext';
import { selectedTemplateRouteRef } from '../../routes';
import { ScaffolderThemeProvider } from '../ScaffolderThemeProvider/ScaffolderThemeProvider';
import {
  DefaultArrayFieldTemplate,
  DefaultObjectFieldTemplate,
} from '../fields';
import DefaultFieldTemplate from '../fields/ArrayFieldTemplate/DefaultFieldTemplate';
import * as widgetOverrides from './WidgetOverrides';
import * as templateOverrides from './TemplateOverrides';
import { transformErrors } from './errorsOverrides';
import { AdditionalStep, Step } from './utils';
import { FieldExtensionOptions } from '../../extensions';
import { useDefaultFields } from './useDefaultFields';

const Form = withTheme(MuiTheme);

type Props = {
  /**
   * Steps for the form, each contains title and form schema
   */
  steps: Step[];
  formData: Record<string, any>;
  onChange: (e: IChangeEvent) => void;
  widgets?: FormProps<any>['widgets'];
  fields?: FieldExtensionOptions<any, any>[];
  additionalSteps?: AdditionalStep[];
  rootStepperStyle?: JsonObject;
  stepContentStyle?: JsonObject;
  viewStepperLabel?: boolean;
  customBackButton?: JSX.Element;
  customNextButton?: JSX.Element;
  customOnSubmitFn?: () => Promise<void>;
};

export const MultiStepJsonForm = (props: Props) => {
  const {
    formData,
    onChange,
    fields,
    widgets,
    steps,
    additionalSteps,
    rootStepperStyle,
    stepContentStyle,
    customBackButton,
    customNextButton,
    customOnSubmitFn,
    viewStepperLabel = true,
  } = props;

  const { templateName } = useRouteRefParams(selectedTemplateRouteRef);
  const theme = useTheme();
  const ref: MutableRefObject<any> = useRef(null);
  const analytics = useAnalytics();
  const [activeStep, setActiveStep] = useState(0);
  const errorApi = useApi(errorApiRef);
  const toolbarHeight = useRef(64);

  // Get current step's schema
  const formStepActive = activeStep < steps.length;
  const currentStep = formStepActive ? steps[activeStep] : null;

  // Use the centralized hook for dependency manager integration
  const { dependencyManager, handleFormChange, handleResetForm } =
    useDependencyManagedForm({
      formData,
      onChange,
      currentStep,
    });

  // Helper function to scroll to the top of the active step
  const scrollToTheTopOfTheActiveStep = () => {
    const stepRefIndex = activeStep > 0 ? activeStep * 2 : 0;
    const yOffset = -(toolbarHeight.current + 10);
    if (
      ref.current &&
      ref.current.children &&
      ref.current.children[stepRefIndex]
    ) {
      const element = ref.current.children[stepRefIndex];
      const yTargetScroll =
        element.getBoundingClientRect().top + window.scrollY + yOffset;
      window.scrollTo({
        top: yTargetScroll,
        behavior: 'instant' as ScrollBehavior,
      });
    }
  };

  const handleNext = () => {
    const stepNum = Math.min(
      activeStep + 1,
      steps.length + (additionalSteps?.length ?? 0),
    );
    setActiveStep(stepNum);
    analytics.captureEvent('click', `Next Step (${stepNum})`);
  };
  const handleBack = () => setActiveStep(Math.max(activeStep - 1, 0));
  const [error, setError] = useState<any>(null);

  const usedFields = useDefaultFields(fields);

  // Helper to setup toolbar height
  useEffect(() => {
    const witboostToolbar = document.getElementById('witboost-toolbar');
    if (witboostToolbar) {
      toolbarHeight.current = witboostToolbar.clientHeight;
    }
  });

  return (
    <ScaffolderThemeProvider autoWidth>
      <Stepper
        style={{
          background: theme.palette.background.default,
          ...rootStepperStyle,
        }}
        activeStep={activeStep}
        orientation="vertical"
        ref={ref}
      >
        {steps.map(
          ({ title, schema, uiSchema, asyncValidate, ...formProps }, index) => {
            const isTitleClickable = activeStep > index;
            return (
              <StepUI key={title}>
                <StepButton
                  style={{
                    cursor: isTitleClickable ? 'pointer' : 'default',
                    display: viewStepperLabel ? 'default' : 'none',
                  }}
                  onClick={() => {
                    if (isTitleClickable) setActiveStep(index);
                  }}
                  disabled={!isTitleClickable}
                >
                  <Typography variant="h6" component="h3">
                    {title}
                  </Typography>
                </StepButton>
                <StepContent
                  style={{ ...stepContentStyle }}
                  key={title}
                  TransitionProps={{
                    // after the transition of the step, we scroll to the top of the active step
                    onExited: scrollToTheTopOfTheActiveStep,
                    // in the onExited we are invoking the scroll method that will have a smooth transition;
                    // given that the default collapse/expanse animation of the step takes a long time, we set it to 0 to reduce the latency for users
                    // so that the overall experience is not a forward-backward scroll of the page
                    timeout: {
                      appear: 0,
                      enter: 0,
                      exit: 0,
                    },
                  }}
                >
                  {/* Manually render the form only for the active step, otherwise when pressing next the previous step form could still be rendered by the stepper and trigger undesired side effects */}
                  {activeStep === index && (
                    <FormExtraConfigContextProvider debounceMs={300}>
                      <WittyFormContextProvider
                        onChange={onChange}
                        formData={formData}
                        formDefinition={steps[activeStep]}
                      >
                        <Form
                          extraErrors={error}
                          templates={{
                            ArrayFieldTemplate: DefaultArrayFieldTemplate,
                            ObjectFieldTemplate: DefaultObjectFieldTemplate,
                            FieldTemplate: DefaultFieldTemplate,
                            ...templateOverrides,
                          }}
                          showErrorList="bottom"
                          fields={usedFields}
                          widgets={{ ...widgetOverrides, ...widgets }}
                          noHtml5Validate
                          formData={formData}
                          onChange={handleFormChange}
                          schema={schema}
                          uiSchema={uiSchema}
                          onSubmit={async e => {
                            setError(null);
                            if (asyncValidate) {
                              const validationResult = await asyncValidate(
                                formData,
                              );
                              if (validationResult) {
                                const keysWithErrors = Object.keys(
                                  validationResult,
                                ).filter(
                                  key =>
                                    validationResult[key]?.__errors?.length,
                                );

                                if (
                                  keysWithErrors.length === 0 &&
                                  e.errors.length === 0
                                ) {
                                  if (typeof customOnSubmitFn === 'function') {
                                    await customOnSubmitFn();
                                  }
                                  handleNext();
                                } else {
                                  setError(validationResult);
                                }
                              } else {
                                if (e.errors.length === 0) {
                                  if (typeof customOnSubmitFn === 'function') {
                                    await customOnSubmitFn();
                                  }
                                  handleNext();
                                }
                              }
                            } else {
                              if (e.errors.length === 0) {
                                if (typeof customOnSubmitFn === 'function') {
                                  await customOnSubmitFn();
                                }
                                handleNext();
                              }
                            }
                          }}
                          {...formProps}
                          formContext={(() => {
                            const ctx = {
                              ...formData,
                              // Add dependency manager to context for pickers (only for active step)
                              dependencyManager,
                              rootSchema: schema,
                            };
                            return ctx;
                          })()}
                          transformErrors={transformErrors}
                        >
                          {customBackButton ?? (
                            <Button
                              disabled={activeStep === 0 && steps.length > 1}
                              onClick={handleBack}
                            >
                              Back
                            </Button>
                          )}
                          {customNextButton ?? (
                            <Button
                              variant="contained"
                              color="primary"
                              type="submit"
                            >
                              Next step
                            </Button>
                          )}
                        </Form>
                      </WittyFormContextProvider>
                    </FormExtraConfigContextProvider>
                  )}
                </StepContent>
              </StepUI>
            );
          },
        )}
        {additionalSteps?.map(
          (
            {
              title,
              component,
              additionalActions,
              backButtonText,
              nextButtonText,
              disableStepButtons,
              customDisableStepButtons,
              resetButtonText,
              analyticsEvent,
              resetButtonColor,
              resetButtonVariant,
              resetAction,
              backAction,
              nextAction,
              stayOnStep,
            },
            index,
          ) => {
            const isTitleClickable = activeStep > index + steps.length;
            return (
              <StepUI key={title}>
                <StepButton
                  style={{
                    cursor: isTitleClickable ? 'pointer' : 'default',
                  }}
                  onClick={() => {
                    if (isTitleClickable) setActiveStep(steps.length + index);
                  }}
                >
                  <Typography variant="h6" component="h3">
                    {title}
                  </Typography>
                </StepButton>
                <StepContent
                  key={title}
                  TransitionProps={{
                    // after the transition of the step, we scroll to the top of the active step
                    onExited: scrollToTheTopOfTheActiveStep,
                    // in the onExited we are invoking the scroll method that will have a smooth transition;
                    // given that the default collapse/expanse animation of the step takes a long time, we set it to 0 to reduce the latency for users
                    // so that the overall experience is not a forward-backward scroll of the page
                    timeout: {
                      appear: 0,
                      enter: 0,
                      exit: 0,
                    },
                  }}
                >
                  <Box>
                    {component}
                    {(resetButtonText || nextButtonText || backButtonText) && (
                      <Box mb={3} />
                    )}
                    <Box style={{ display: 'flex', alignItems: 'center' }}>
                      {backButtonText && (
                        <Button
                          disabled={
                            customDisableStepButtons?.disableBack ??
                            disableStepButtons
                          }
                          onClick={() => {
                            if (backAction) backAction();
                            handleBack();
                          }}
                        >
                          {backButtonText}
                        </Button>
                      )}
                      {resetButtonText && (
                        <Button
                          variant={resetButtonVariant}
                          color={resetButtonColor}
                          onClick={() => {
                            if (resetAction) resetAction();

                            handleResetForm();
                            setActiveStep(0);
                          }}
                          disabled={
                            customDisableStepButtons?.disableReset ??
                            disableStepButtons
                          }
                        >
                          {resetButtonText}
                        </Button>
                      )}
                      {nextButtonText && (
                        <Button
                          variant="contained"
                          color="primary"
                          disabled={
                            customDisableStepButtons?.disableNext ??
                            disableStepButtons
                          }
                          onClick={async () => {
                            try {
                              if (nextAction) await nextAction();
                              if (analyticsEvent) {
                                analytics.captureEvent(
                                  analyticsEvent,
                                  formData.name || `new ${templateName}`,
                                );
                              }
                              if (!stayOnStep) handleNext();
                            } catch (err) {
                              errorApi.post(err);
                            }
                          }}
                        >
                          {nextButtonText}
                        </Button>
                      )}
                      {additionalActions?.map(action => (
                        <Fragment key={action.key}>{action}</Fragment>
                      ))}
                    </Box>
                  </Box>
                </StepContent>
              </StepUI>
            );
          },
        )}
      </Stepper>
    </ScaffolderThemeProvider>
  );
};
